import type { StackTowerConfig } from './rules.ts';

/** A block's horizontal extent — `min`/`max` are absolute positions on the track, not width/centre. */
export interface BlockFootprint {
  readonly min: number;
  readonly max: number;
}

export interface OverhangPiece extends BlockFootprint {
  readonly side: 'left' | 'right';
}

export type PlacementOutcome =
  | { readonly kind: 'miss'; readonly movingBlock: BlockFootprint }
  | {
      readonly kind: 'hit';
      readonly perfect: boolean;
      /** Becomes the next moving block's spawn footprint. Equal to `previous` on a perfect hit (no cut). */
      readonly newFootprint: BlockFootprint;
      /** The unsupported piece that fell away. Null on a perfect hit, or when the offset happens to land exactly on an edge. */
      readonly overhang: OverhangPiece | null;
      readonly offsetFromPreviousCentre: number;
    };

export function footprintWidth(f: BlockFootprint): number {
  return f.max - f.min;
}

export function footprintCentre(f: BlockFootprint): number {
  return (f.min + f.max) / 2;
}

/**
 * The correctness-critical overlap/cut/perfect math, implemented exactly as
 * the spec's own pseudocode (§2-§3) rather than approximated:
 *
 *   overlapMin = max(previousMin, currentMin)
 *   overlapMax = min(previousMax, currentMax)
 *   overlapSize = overlapMax - overlapMin
 *
 * `overlapSize <= 0` is a miss — exact-zero (blocks exactly touching, no
 * real overlap) counts as a miss, not a hit, per the spec's literal formula.
 * This must stay `<=`, not `<`: it's the one boundary a naive implementation
 * most often gets backwards.
 *
 * Perfect tolerance (spec §3) is strictly `<`, not `<=` — "below a
 * configurable tolerance", the spec's literal wording. On a perfect hit the
 * full previous footprint survives (no cut at all), not the raw overlap.
 *
 * Overhang is geometrically at most one piece, never both sides at once:
 * every moving block spawns at exactly the width of the footprint beneath
 * it (`newFootprint` becomes the next spawn), so `current` and `previous`
 * are always equal-width rectangles — a shift in one direction can only
 * ever create overhang on that side. `OverhangPiece` is still a generic
 * one-of-two-sides shape (not hardcoded), so this stays correct if a future
 * variant ever spawns unequal-width blocks; it just can't happen today.
 */
export function resolvePlacement(
  previous: BlockFootprint,
  current: BlockFootprint,
  config: Pick<StackTowerConfig, 'perfectToleranceUnits'>,
): PlacementOutcome {
  const overlapMin = Math.max(previous.min, current.min);
  const overlapMax = Math.min(previous.max, current.max);
  const overlapSize = overlapMax - overlapMin;

  if (overlapSize <= 0) {
    return { kind: 'miss', movingBlock: current };
  }

  const offsetFromPreviousCentre = footprintCentre(current) - footprintCentre(previous);

  if (Math.abs(offsetFromPreviousCentre) < config.perfectToleranceUnits) {
    return { kind: 'hit', perfect: true, newFootprint: previous, overhang: null, offsetFromPreviousCentre };
  }

  const newFootprint: BlockFootprint = { min: overlapMin, max: overlapMax };
  let overhang: OverhangPiece | null = null;
  if (current.min < overlapMin) {
    overhang = { min: current.min, max: overlapMin, side: 'left' };
  } else if (current.max > overlapMax) {
    overhang = { min: overlapMax, max: current.max, side: 'right' };
  }

  return { kind: 'hit', perfect: false, newFootprint, overhang, offsetFromPreviousCentre };
}
